<template>
    <v-container class="py-12 overview-page">
        <div class="overview-header">
            <div>
                <div class="overview-breadcrumb">
                    <span>Painel administrativo</span>
                    <span>/</span>
                    <strong>Visão geral</strong>
                </div>
                <h1>Visão geral</h1>
                <p>Acompanhe viagens, valores, motoristas e frota no período selecionado.</p>
            </div>
            <div class="overview-header-actions">
                <span v-if="dataset.lastUpdatedAt">Atualizado às {{ dataset.lastUpdatedAt }}</span>
                <v-btn outlined color="primary" elevation="0" :loading="dataset.loading" @click="exportTripReport()">
                    <v-icon left small>mdi-microsoft-excel</v-icon>Exportar relatório
                </v-btn>
                <v-btn outlined color="primary" elevation="0" :loading="dataset.loading" @click="exportTripReportByDriver()">
                    <v-icon left small>mdi-account-arrow-down-outline</v-icon>Por motorista
                </v-btn>
                <v-btn outlined color="primary" elevation="0" :loading="dataset.exportingPartnerReport"
                    @click="exportPartnerDriverReport()">
                    <v-icon left small>mdi-cash-multiple</v-icon>Parceiros
                </v-btn>
            </div>
        </div>

        <v-card class="overview-filter-card">
            <v-card-text>
                <div class="overview-section-heading">
                    <v-icon small>mdi-filter-outline</v-icon>
                    <strong>Filtros de busca</strong>
                </div>
                <v-row dense align="end">
                    <v-col cols="12" sm="6" lg>
                        <span class="filter-label">Cliente</span>
                        <v-select :disabled="dataset.loading" v-model="dataset.selectedClient" :items="dataset.clientList"
                            item-text="name" item-value="id" placeholder="Todos os clientes" outlined dense clearable
                            hide-details></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" lg>
                        <span class="filter-label">Motorista</span>
                        <v-select :disabled="dataset.loading || !dataset.driverList.length" v-model="dataset.selectedDriver"
                            :items="dataset.driverList" item-text="name" item-value="id" placeholder="Todos os motoristas"
                            outlined dense clearable hide-details></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" lg>
                        <span class="filter-label">Totalizadores</span>
                        <v-select :disabled="dataset.loading" v-model="dataset.totalizerScope"
                            :items="dataset.totalizerScopes" item-text="label" item-value="value"
                            outlined dense hide-details @change="loadReportCount()"></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" lg>
                        <span class="filter-label">Período</span>
                        <v-select :disabled="dataset.loading" v-on:change="normalizeRangeByType" dense
                            :items="dataset.periodTypes" v-model="dataset.filterSelected" item-value="type"
                            item-text="label" outlined hide-details></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" lg>
                        <span class="filter-label">Data início</span>
                        <v-dialog ref="startDateDialog" v-model="dataset.datePickerStartDialog" persistent width="290px">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field :value="$moment(dataset.rangeDate.startDate).format('DD/MM/YYYY')"
                                    :disabled="dataset.filterSelected != 9" readonly dense v-bind="attrs"
                                    v-on="dataset.filterSelected == 9 ? on : null" outlined hide-details></v-text-field>
                            </template>
                            <v-date-picker locale="pt-br" v-model="dataset.rangeDate.startDate" scrollable>
                                <v-spacer></v-spacer>
                                <v-btn text color="primary" @click="dataset.datePickerStartDialog = false">Cancelar</v-btn>
                                <v-btn text color="primary"
                                    @click="$refs.startDateDialog.save(dataset.rangeDate.startDate); dataset.datePickerStartDialog = false">
                                    Salvar
                                </v-btn>
                            </v-date-picker>
                        </v-dialog>
                    </v-col>
                    <v-col cols="12" sm="6" lg>
                        <span class="filter-label">Data fim</span>
                        <v-dialog ref="endDateDialog" v-model="dataset.datePickerEndDialog" persistent width="290px">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field :value="$moment(dataset.rangeDate.endDate).format('DD/MM/YYYY')"
                                    :disabled="dataset.filterSelected != 9" readonly dense v-bind="attrs"
                                    v-on="dataset.filterSelected == 9 ? on : null" outlined hide-details></v-text-field>
                            </template>
                            <v-date-picker locale="pt-br" v-model="dataset.rangeDate.endDate" scrollable>
                                <v-spacer></v-spacer>
                                <v-btn text color="primary" @click="dataset.datePickerEndDialog = false">Cancelar</v-btn>
                                <v-btn text color="primary"
                                    @click="$refs.endDateDialog.save(dataset.rangeDate.endDate); dataset.datePickerEndDialog = false">
                                    Salvar
                                </v-btn>
                            </v-date-picker>
                        </v-dialog>
                    </v-col>
                    <v-col cols="12" lg="2">
                        <v-btn block color="primary" elevation="0" :loading="dataset.loading" @click="loadData()">
                            Aplicar filtros
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <div class="overview-context">
            <div>
                <span>Analisando</span>
                <strong>{{ selectedClientName }}</strong>
                <span v-if="selectedDriverName">· {{ selectedDriverName }}</span>
                <span>{{ $moment(dataset.rangeDate.startDate).format('DD/MM/YYYY') }} a
                    {{ $moment(dataset.rangeDate.endDate).format('DD/MM/YYYY') }}</span>
            </div>
            <button type="button" :disabled="dataset.loading" @click="clearFilters">Limpar filtros</button>
        </div>

        <div class="stat-grid overview-kpis">
            <stat-card v-for="(item, index) in dataset.items" :key="index" :loading="dataset.loading"
                :label="item.title" :value="item.value" :hint="item.subtitle" :icon="kpiIcon(item.title)" />
        </div>

        <section class="section-grid mt-4">
            <v-card class="section-card section-grid__wide">
                <v-card-title>
                    <div>
                        <span>Viagens por data</span>
                        <small>Volume diário</small>
                    </div>
                    <v-icon color="#2563eb">mdi-chart-bar</v-icon>
                </v-card-title>
                <v-card-text>
                    <apexchart type="bar" height="320" :options="dataset.tripByDateChartOptions.chartOptions"
                        :series="dataset.tripByDateChartOptions.series"></apexchart>
                </v-card-text>
            </v-card>

            <v-card class="section-card section-grid__wide">
                <v-card-title>
                    <div>
                        <span>Valores por data</span>
                        <small>Total financeiro</small>
                    </div>
                    <v-icon color="#10b981">mdi-cash-multiple</v-icon>
                </v-card-title>
                <v-card-text>
                    <apexchart type="bar" height="320" :options="dataset.serviceFinancialChartOptions.chartOptions"
                        :series="dataset.serviceFinancialChartOptions.series"></apexchart>
                </v-card-text>
            </v-card>

            <v-card class="section-card section-grid__wide">
                <v-card-title>
                    <div>
                        <span>Viagens por localidade</span>
                        <small>Viagens concluídas no período</small>
                    </div>
                    <v-btn icon small color="primary" :loading="dataset.exportingLocationXLSX"
                        :disabled="dataset.exportingLocationXLSX" @click="exportLocationXLSX">
                        <v-icon size="20">mdi-file-excel-outline</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <apexchart type="bar" height="320" :options="dataset.tripByLocationChartOptions.chartOptions"
                        :series="dataset.tripByLocationChartOptions.series"></apexchart>
                </v-card-text>
            </v-card>

            <v-card class="section-card">
                <v-card-title>
                    <div>
                        <span>Viagens por solicitante</span>
                        <small>Demanda interna</small>
                    </div>
                    <v-icon color="#f59e0b">mdi-account-group-outline</v-icon>
                </v-card-title>
                <v-card-text>
                    <apexchart type="bar" height="300" :options="dataset.serviceRequesterChartOptions.chartOptions"
                        :series="dataset.serviceRequesterChartOptions.series"></apexchart>
                </v-card-text>
            </v-card>

            <v-card class="section-card">
                <v-card-title>
                    <div>
                        <span>Centro de custo</span>
                        <small>Distribuição operacional</small>
                    </div>
                    <v-btn icon small color="primary" @click="exportCostCenterCSV">
                        <v-icon size="20">mdi-download-outline</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <apexchart type="bar" height="300" :options="dataset.serviceCostCenterChartOptions.chartOptions"
                        :series="dataset.serviceCostCenterChartOptions.series"></apexchart>
                </v-card-text>
            </v-card>

            <v-card class="section-card">
                <v-card-title>
                    <div>
                        <span>Viagens por motorista</span>
                        <small>Produtividade da equipe</small>
                    </div>
                    <v-icon color="#0f766e">mdi-steering</v-icon>
                </v-card-title>
                <v-card-text>
                    <apexchart type="bar" height="300" :options="dataset.tripByDriverChartOptions.chartOptions"
                        :series="dataset.tripByDriverChartOptions.series"></apexchart>
                </v-card-text>
            </v-card>

            <v-card class="section-card">
                <v-card-title>
                    <div>
                        <span>Motoristas parceiros</span>
                        <small>Viagens e recebimento</small>
                    </div>
                    <v-icon color="#0f766e">mdi-cash-multiple</v-icon>
                </v-card-title>
                <v-card-text>
                    <apexchart type="bar" height="300" :options="dataset.partnerDriverChartOptions.chartOptions"
                        :series="dataset.partnerDriverChartOptions.series"></apexchart>
                </v-card-text>
            </v-card>

            <v-card class="section-card">
                <v-card-title>
                    <div>
                        <span>Viagens por veículo</span>
                        <small>Uso da frota</small>
                    </div>
                    <v-icon color="#dc2626">mdi-car-estate</v-icon>
                </v-card-title>
                <v-card-text>
                    <apexchart type="bar" height="300" :options="dataset.tripByVehicleChartOptions.chartOptions"
                        :series="dataset.tripByVehicleChartOptions.series"></apexchart>
                </v-card-text>
            </v-card>
        </section>
    </v-container>
</template>

<script>
import 'moment/locale/pt-br';
import VueApexCharts from 'vue-apexcharts'
import { eventHub } from '@/main';

import axios from "axios"

export default {
    components: {
        apexchart: VueApexCharts,
    },

    data: () => ({
        dataset: {

            title: "",
            subtitle: "",
            items: [],

            driverList: [],
            selectedDriver: null,
            totalizerScope: 'period',
            totalizerScopes: [
                { label: 'Período filtrado', value: 'period' },
                { label: 'Total geral', value: 'total' },
            ],

            operation: null,
            loading: false,
            exportingLocationXLSX: false,
            exportingPartnerReport: false,
            partnerPaymentSummary: {
                total: 0,
                trips: 0,
                drivers: 0,
                average: 0,
            },
            totalTripCount: 0,
            totalPassengerCount: 0,
            totalSale: {
                qtdCiclos: 0,
                qtdVendas: 0,
                ticketMedio: 0.00,
                valorTotal: 0.00,
            },
            createdEvent: false,
            datePickerStartDialog: false,
            datePickerEndDialog: false,
            periodTypes: [
                {
                    label: 'Hoje',
                    type: 1
                },
                {
                    label: 'Ontem',
                    type: 2
                },
                {
                    label: '7 dias',
                    type: 3
                },
                {
                    label: '15 dias',
                    type: 4
                },
                {
                    label: '30 dias',
                    type: 5
                },
                {
                    label: 'Esse mês',
                    type: 6
                },
                {
                    label: 'Mês passado',
                    type: 7
                },
                {
                    label: 'Últimos 3 meses',
                    type: 8
                },
                {
                    label: 'Personalizado',
                    type: 9
                },
            ],

            filterSelected: 3,
            rangeDate: { startDate: null, endDate: null },
            timeResume: [],
            cycleResume: [],
            saleBrand: [],
            clientList: [],
            selectedClient: null,
            lastUpdatedAt: null,

            tripByDateChartOptions: {
                chartOptions: {
                    chart: {
                        height: 300,
                        toolbar: {
                            show: false
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories: []
                    }
                },
                series: [{
                    name: 'Viagens por data',
                    data: [],
                }]
            },
            serviceFinancialChartOptions: {
                chartOptions: {
                    chart: {
                        height: 300,
                        toolbar: {
                            show: false
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories: []
                    }
                },
                series: [{
                    name: 'Valores das viagens por data',
                    data: [],
                }]
            },
            serviceRequesterChartOptions: {
                chartOptions: {
                    chart: {
                        height: 300,
                        toolbar: {
                            show: false
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories: []
                    }
                },
                series: [{
                    name: 'Viagens por solicitantes',
                    data: [],
                }]
            },
            serviceCostCenterChartOptions: {
                chartOptions: {
                    chart: {
                        height: 300,
                        toolbar: {
                            show: false
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories: []
                    }
                },
                series: [{
                    name: 'Viagens por centro de custo',
                    data: [],
                }]
            },
            tripByLocationChartOptions: {
                chartOptions: {
                    chart: {
                        height: 300,
                        toolbar: {
                            show: false
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories: []
                    }
                },
                series: [{
                    name: 'Viagens por localidade',
                    data: [],
                }]
            },
            tripByDriverChartOptions: {
                chartOptions: {
                    chart: {
                        type: 'bar',
                        height: 300,
                        stacked: true,
                        toolbar: {
                            show: false
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories: []
                    }
                },
                series: [{
                    name: 'Motorista',
                    data: []
                }]
            },
            partnerDriverChartOptions: {
                chartOptions: {
                    chart: {
                        type: 'bar',
                        height: 300,
                        toolbar: {
                            show: false
                        },
                    },
                    colors: ['#2563eb', '#10b981'],
                    plotOptions: {
                        bar: {
                            columnWidth: '46%',
                        },
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    xaxis: {
                        type: 'category',
                        categories: []
                    },
                    yaxis: [
                        {
                            title: { text: 'Viagens' },
                        },
                        {
                            opposite: true,
                            title: { text: 'Valor' },
                            labels: {
                                formatter: (value) => Number(value || 0).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                    maximumFractionDigits: 0,
                                }),
                            },
                        },
                    ],
                    tooltip: {
                        shared: true,
                        intersect: false,
                        y: [
                            {
                                formatter: (value) => `${Number(value || 0)} viagens`,
                            },
                            {
                                formatter: (value) => Number(value || 0).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                }),
                            },
                        ],
                    },
                },
                series: [
                    {
                        name: 'Viagens',
                        data: [],
                    },
                    {
                        name: 'Valor total',
                        data: [],
                    },
                ]
            },
            tripByVehicleChartOptions: {
                chartOptions: {
                    chart: {
                        type: 'bar',
                        height: 300,
                        stacked: true,
                        toolbar: {
                            show: false
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories: []
                    }
                },
                series: [{
                    name: 'Veículo',
                    data: []
                }]
            },
            saleBrandResumeChartOptions: {
                chartOptions: {
                    chart: {
                        type: 'donut',
                        height: 300,
                        responsive: false
                    },
                    colors: ['#55baaa', '#1e48ab', '#eba33e'],
                    labels: [],
                    tooltip: {
                        fillSeriesColor: true,
                        y: {
                            formatter: function (value, opt) {
                                return 'R$ ' + parseFloat(value.toString()).toFixed(2);
                            },
                        }
                    }
                },
                series: [0, 0, 0, 0]
            },
            paymentTypeChartOptions: {
                chartOptions: {
                    chart: {
                        type: 'donut',
                        height: 300,
                        responsive: false
                    },
                    colors: ['#55baaa', '#1e48ab', '#eba33e'],
                    labels: [],
                    tooltip: {
                        fillSeriesColor: true,
                        y: {
                            formatter: function (value, opt) {
                                return 'R$ ' + parseFloat(value.toString()).toFixed(2);
                            },
                        }
                    }
                },
                series: [0, 0, 0, 0]
            },

            //Gráfico já existente
            saleChartOptions: {
                title: null,
                series: [
                    {
                        name: "",
                        data: [1000, 2000, 1042, 1230, 1881, 450, 2199],
                        type: 'column',
                        color: '#3BA5DB'
                    },
                ],
                xAxis: {
                    categories: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
                },
                yAxis: {
                    text: "",
                    visible: false
                },
                plotOptions: {
                    column: {
                        borderRadius: 5,
                    }
                },
                credits: {
                    visible: false
                },
                responsive: {}
            }
        }
    }),

    mounted: function () {
        this.user = this.$root.user;
        this.loadData();
        this.loadDrivers();
        this.loadClientList();
        eventHub.$on('CHANGE-OPERATION', (value) => {
            this.getSelectedOperation();
            this.loadData()
        })
    },

    destroyed() {
        eventHub.$off('CHANGE-OPERATION')
    },

    created() {
        this.createdEvent = true
    },

    computed: {
        selectedClientName() {
            const client = this.dataset.clientList.find(item => item.id === this.dataset.selectedClient);
            return client && this.dataset.selectedClient ? client.name : 'Todos os clientes';
        },

        selectedDriverName() {
            const driver = this.dataset.driverList.find(item => item.id === this.dataset.selectedDriver);
            return driver && this.dataset.selectedDriver ? driver.name : '';
        },
    },

    methods: {
        kpiIcon(title) {
            const normalized = (title || '').toLowerCase();

            if (normalized.includes('custo') || normalized.includes('recebimento') || normalized.includes('valor')) return 'mdi-cash-multiple';
            if (normalized.includes('ticket')) return 'mdi-ticket-percent-outline';
            if (normalized.includes('cancel')) return 'mdi-close-circle-outline';
            if (normalized.includes('motorista')) return 'mdi-steering';
            if (normalized.includes('cliente') || normalized.includes('empresa')) return 'mdi-domain';
            if (normalized.includes('passageiro')) return 'mdi-account-multiple-outline';

            return 'mdi-map-marker-path';
        },

        clearFilters() {
            this.dataset.selectedClient = null;
            this.dataset.selectedDriver = null;
            this.dataset.totalizerScope = 'period';
            this.dataset.filterSelected = 3;
            this.loadData();
        },

        getDashboardDate(item) {
            return item.service_date || item.date;
        },

        getDashboardFilterQuery(includeTotalizerScope = false) {
            const params = [
                `start=${this.dataset.rangeDate.startDate}`,
                `end=${this.dataset.rangeDate.endDate}`,
            ];

            if (this.dataset.selectedClient) {
                params.push(`client_id=${this.dataset.selectedClient}`);
            }

            if (this.dataset.selectedDriver) {
                params.push(`driver_id=${this.dataset.selectedDriver}`);
            }

            if (includeTotalizerScope) {
                params.push(`count_scope=${this.dataset.totalizerScope}`);
            }

            return `?${params.join('&')}`;
        },

        async loadClientList() {
            try {
                const response = await axios.get('/api/customers');
                this.dataset.clientList = response.data || [];
                this.dataset.clientList.unshift({ id: null, name: 'Todos os Clientes' });
                this.dataset.selectedClient = null;

            } catch (error) {
                console.error('Erro ao carregar lista de clientes:', error);
                this.$dialog.notify.error('Erro ao carregar clientes', { position: 'top-right', timeout: 3000 });
            }
        },
     
        async loadDrivers() {
            try {
                const response = await axios.get('/api/v2/drivers?limit=1000');
                this.dataset.driverList = response.data.content.data || [];
                this.dataset.driverList.unshift({ id: null, name: 'Todos os motoristas' });
                this.dataset.selectedDriver = null;

            } catch (error) {
                console.error('Erro ao carregar lista de motoristas:', error);
                this.$dialog.notify.error('Erro ao carregar motoristas', { position: 'top-right', timeout: 3000 });
            }
        },

        async loadData() {
            this.normalizeRangeByType();
            this.dataset.loading = true;

            const filterQuery = this.getDashboardFilterQuery();

            try {
                await this.loadReporServices(filterQuery);
                await this.loadReportDriverService(filterQuery);
                await this.loadReportVehicleService(filterQuery);
                await this.loadReportFinancialService(filterQuery);
                await this.loadReportRequesterService(filterQuery);
                await this.loadReportCostCenterService(filterQuery);
                await this.loadReportLocationService(filterQuery);
                await this.loadReportCount();
                await this.loadPartnerPaymentsSummary();
                await this.loadPartnerDriverChart();
                this.dataset.lastUpdatedAt = this.$moment().format('HH:mm');
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataset.loading = false;
            }
        },

        async loadReporServices(filterQuery = this.getDashboardFilterQuery()) {
            const result = await axios.get(`/api/v2/dashboards/trip-dashboard${filterQuery}`);
            const items = result.data.content.items
                .sort((a, b) => new Date(this.getDashboardDate(a)) - new Date(this.getDashboardDate(b)))
                .slice(-10);

            const categories = [];
            const data = [];
            items.forEach(item => {
                const formattedDate = this.$moment(this.getDashboardDate(item)).format('DD/MM/YYYY');
                categories.push(formattedDate);
                data.push(item.total);
            });
            const name = result.data.content.title;

            this.dataset.tripByDateChartOptions = {
                chartOptions: {
                    chart: { type: 'bar' },
                    xaxis: { categories: categories },
                    yaxis: { title: { text: 'Total' } }
                },
                series: [{ name: name, data: data }]
            };

        },

        async loadReportDriverService(filterQuery = this.getDashboardFilterQuery()) {
            const result = await axios.get(`/api/v2/dashboards/trip-driver-dashboard${filterQuery}`);
            const items = result.data.content.items.slice(-10);
            const categories = [];
            const data = [];
            items.forEach(item => {
                categories.push(item.driver_name);
                data.push(item.total);
            });
            const name = result.data.content.title;
            this.dataset.tripByDriverChartOptions = {
                chartOptions: {
                    chart: { type: 'bar' },
                    xaxis: { categories: categories },
                    yaxis: { title: { text: 'Total' } }
                },
                series: [{ name: name, data: data }]
            };
        },

        async loadReportVehicleService(filterQuery = this.getDashboardFilterQuery()) {
            const result = await axios.get(`/api/v2/dashboards/trip-vehicle-dashboard${filterQuery}`);
            const items = result.data.content.items.slice(-10);
            const categories = [];
            const data = [];
            items.forEach(item => {
                categories.push(item.vehicle_name);
                data.push(item.total);
            });
            const name = result.data.content.title;
            this.dataset.tripByVehicleChartOptions = {
                chartOptions: {
                    chart: { type: 'bar' },
                    xaxis: { categories: categories },
                    yaxis: { title: { text: 'Total' } }
                },
                series: [{ name: name, data: data }]
            };
        },

        async loadReportFinancialService(filterQuery = this.getDashboardFilterQuery()) {
            const result = await axios.get(`/api/v2/dashboards/trip-financial-dashboard${filterQuery}`);
            const items = result.data.content.items;
            const categories = [];
            const data = [];
            items.forEach(item => {
                categories.push(this.$moment(this.getDashboardDate(item)).format('DD/MM/YYYY'));
                data.push(item.total_value);
            });
            const name = result.data.content.title;
            this.dataset.serviceFinancialChartOptions = {
                chartOptions: {
                    chart: { type: 'bar' },
                    xaxis: { categories: categories },
                    yaxis: { title: { text: 'Total' } }
                },
                series: [{ name: name, data: data }]
            };
        },

        async loadReportRequesterService(filterQuery = this.getDashboardFilterQuery()) {
            const result = await axios.get(`/api/v2/dashboards/trip-requester-dashboard${filterQuery}`);
            const items = result.data.content.items;
            const categories = [];
            const data = [];
            items.forEach(item => {
                categories.push(item.requester_name);
                data.push(item.total);
            });
            const name = result.data.content.title;
            this.dataset.serviceRequesterChartOptions = {
                chartOptions: {
                    chart: { type: 'bar' },
                    xaxis: { categories: categories },
                    yaxis: { title: { text: 'Total' } }
                },
                series: [{ name: name, data: data }]
            };
        },

        async loadReportCostCenterService(filterQuery = this.getDashboardFilterQuery()) {
            const result = await axios.get(`/api/v2/dashboards/trip-costcenter-dashboard${filterQuery}`);
            const items = result.data.content.items;
            const categories = [];
            const data = [];
            for (let item of items) {
                if (!item.cost_center) continue;
                categories.push(item.cost_center.toString().trim().replaceAll('"', ''));
                data.push(item.total);
            }
            const name = result.data.content.title;
            this.dataset.serviceCostCenterChartOptions = {
                chartOptions: {
                    chart: { type: 'bar' },
                    xaxis: { categories: categories },
                    yaxis: { title: { text: 'Total' } }
                },
                series: [{ name: name, data: data }]
            };
        },

        async loadReportLocationService(filterQuery = this.getDashboardFilterQuery()) {
            const result = await axios.get(`/api/v2/dashboards/trip-location-dashboard${filterQuery}`);
            const items = result.data.content.items.slice(0, 10);
            const categories = [];
            const data = [];
            for (let item of items) {
                if (!item.location_name) continue;
                categories.push(item.location_name.toString().trim().replaceAll('"', ''));
                data.push(item.total);
            }
            const name = result.data.content.title;
            this.dataset.tripByLocationChartOptions = {
                chartOptions: {
                    chart: { type: 'bar' },
                    xaxis: { categories: categories },
                    yaxis: { title: { text: 'Total' } }
                },
                series: [{ name: name, data: data }]
            };
        },

        async loadReportCount() {

            const result = await axios.get(`/api/v2/dashboards/count-v2-dashboard${this.getDashboardFilterQuery(true)}`);

            this.dataset.items = result.data.content.items
            this.dataset.title = result.data.content.title
            this.dataset.subtitle = result.data.content.subtitle

        },

        async loadPartnerPaymentsSummary() {
            const response = await axios.get(`/api/v2/dashboards/partner-payment-summary-dashboard${this.getDashboardFilterQuery()}`);

            // Sem permissão para valores: não mostra o card
            if (response.data.content && response.data.content.restricted) {
                this.dataset.items = this.dataset.items.filter((item) => item.key !== 'partner-payments');
                return;
            }

            const summary = (response.data.content && response.data.content.summary) || {};

            this.dataset.partnerPaymentSummary = {
                total: Number(summary.total || 0),
                trips: Number(summary.trips || 0),
                drivers: Number(summary.drivers || 0),
                average: Number(summary.average || 0),
            };

            this.upsertPartnerPaymentMetric();
        },

        async loadPartnerDriverChart() {
            const response = await axios.get(`/api/v2/dashboards/partner-driver-dashboard${this.getDashboardFilterQuery()}`);
            const items = response.data.content.items || [];
            const categories = items.map((item) => item.driver_name || 'Sem motorista');
            const trips = items.map((item) => Number(item.total_trips || 0));
            const values = items.map((item) => Number(item.total_value || 0));

            this.dataset.partnerDriverChartOptions = {
                ...this.dataset.partnerDriverChartOptions,
                chartOptions: {
                    ...this.dataset.partnerDriverChartOptions.chartOptions,
                    xaxis: {
                        type: 'category',
                        categories,
                    },
                },
                series: [
                    {
                        name: 'Viagens',
                        data: trips,
                    },
                    {
                        name: 'Valor total',
                        data: values,
                    },
                ],
            };
        },

        upsertPartnerPaymentMetric() {
            const items = this.dataset.items.filter((item) => item.key !== 'partner-payments');

            items.push({
                key: 'partner-payments',
                title: 'Recebimento parceiros',
                subtitle: `${this.dataset.partnerPaymentSummary.trips} viagens / ${this.dataset.partnerPaymentSummary.drivers} motoristas`,
                value: this.formatCurrency(this.dataset.partnerPaymentSummary.total),
            });

            this.dataset.items = items;
        },

        formatCurrency(value) {
            return Number(value || 0).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            });
        },

        normalizedDateRange(dates) {
            return dates.map((x) => this.$moment(x).format('DD/MM/YYYY')).join(" - ")
        },

        normalizeRangeByType() {
            switch (this.dataset.filterSelected) {
                case 1:
                    this.dataset.rangeDate.startDate = this.$moment().format('YYYY-MM-DD');
                    this.dataset.rangeDate.endDate = this.$moment().format('YYYY-MM-DD');
                    break;
                case 2:
                    this.dataset.rangeDate.startDate = this.$moment().subtract(1, 'd').format('YYYY-MM-DD');
                    this.dataset.rangeDate.endDate = this.$moment().subtract(1, 'd').format('YYYY-MM-DD');
                    break;
                case 3:
                    this.dataset.rangeDate.startDate = this.$moment().subtract(7, 'd').startOf('day').format('YYYY-MM-DD');
                    this.dataset.rangeDate.endDate = this.$moment().format('YYYY-MM-DD');
                    break;
                case 4:
                    this.dataset.rangeDate.startDate = this.$moment().subtract(15, 'd').format('YYYY-MM-DD');
                    this.dataset.rangeDate.endDate = this.$moment().format('YYYY-MM-DD');
                    break;
                case 5:
                    this.dataset.rangeDate.startDate = this.$moment().subtract(30, 'd').format('YYYY-MM-DD');
                    this.dataset.rangeDate.endDate = this.$moment().format('YYYY-MM-DD');
                    break;
                case 6:
                    this.dataset.rangeDate.startDate = this.$moment().startOf('month').format('YYYY-MM-DD');
                    this.dataset.rangeDate.endDate = this.$moment().endOf('month').format('YYYY-MM-DD');
                    break;
                case 7:
                    this.dataset.rangeDate.startDate = this.$moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
                    this.dataset.rangeDate.endDate = this.$moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
                    break;
                case 8:
                    this.dataset.rangeDate.startDate = this.$moment().subtract(3, 'month').format('YYYY-MM-DD');
                    this.dataset.rangeDate.endDate = this.$moment(new Date()).format('YYYY-MM-DD');
                    break;
                default:
                    break;
            }
        },

        changeFilter(type) {
            this.dataset.filterSelected = type;
            if (type != 5) this.loadData();
        },

        getSelectedOperation() {
            this.dataset.operation = localStorage.getItem('operation');
        },

        async exportCostCenterCSV() {
            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja exportar os dados para CSV?',
                title: 'Exportar dados',
                actions: {
                    false: 'Cancelar',
                    true: 'Exportar'
                }
            })

            if (!proceedAction) return;

            const result = await axios.get(`/api/v2/dashboards/trip-costcenter-dashboard${this.getDashboardFilterQuery()}`);
            const items = result.data.content.items;
            let newData = [];

            for (let item of items) {
                if (!item.cost_center) continue;
                let data = {
                    "CC": item.cost_center.toString().trim().replaceAll('"', ''),
                    "TOTAL": item.total
                };
                newData.push(data);
            }

            if (!newData.length) {
                this.$dialog.notify.info("Nenhum centro de custo encontrado para o filtro selecionado.", { position: "top-right", timeout: 3000 });
                return;
            }

            const header = Object.keys(newData[0]);
            const csvContent = [header.join(';')];

            newData.forEach(item => {
                const row = header.map(key => {
                    const value = item[key];
                    return typeof value === 'object' && value !== null ? value.join(';') : value;
                }).join(';');
                csvContent.push(row);
            });

            const csv = csvContent.join('\r\n');
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', 'Relatório por centro de custos.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.$dialog.notify.info("Arquivo CSV gerado com sucesso!", { position: "top-right", timeout: 2000 });
        },

        async exportLocationXLSX() {
            this.dataset.exportingLocationXLSX = true;

            try {
                const response = await axios.get('/api/v2/trips/location/report', {
                    params: {
                        start: this.dataset.rangeDate.startDate,
                        end: this.dataset.rangeDate.endDate,
                        customer: this.dataset.selectedClient,
                        driver: this.dataset.selectedDriver
                    },
                    responseType: 'blob'
                });

                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_viagens_localidade.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                console.error('Erro ao exportar relatório por localidade:', err);
                this.$dialog.notify.error("Erro ao exportar relatório por localidade", {
                    position: "top-right",
                    timeout: 3000
                });
            } finally {
                this.dataset.exportingLocationXLSX = false;
            }
        },

        async exportTripReport() {
            try {
                // Painel administrativo: sem cliente selecionado exporta todos
                const response = await axios.get('/api/v2/trips/report', {
                    params: {
                        start: this.dataset.rangeDate.startDate,
                        end: this.dataset.rangeDate.endDate,
                        customer: this.dataset.selectedClient
                    },
                    responseType: 'blob'
                });

                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_viagens.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();

            } catch (err) {
                console.error('Erro ao baixar relatório:', err);
            }
        },

        async exportTripReportByDriver() {
            try {
                // Cliente é opcional (vazio = todos); o relatório é de um motorista
                if (!this.dataset.selectedDriver) {
                    this.$dialog.notify.info("Selecione um motorista para exportar o relatório por motorista.", {
                        position: "top-right",
                        timeout: 4000
                    });
                    return;
                }

                const response = await axios.get(`/api/v2/trips/driver/report`, {
                    params: {
                        start: this.dataset.rangeDate.startDate,
                        end: this.dataset.rangeDate.endDate,
                        customer: this.dataset.selectedClient,
                        driver: this.dataset.selectedDriver
                    },
                    responseType: 'blob'
                });

                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_viagens_motorista.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();

            } catch (err) {
                console.error('Erro ao baixar relatório por motorista:', err);
                this.$dialog.notify.error("Erro ao exportar relatório por motorista", {
                    position: "top-right",
                    timeout: 3000
                });
            }
        },

        async exportPartnerDriverReport() {
            this.dataset.exportingPartnerReport = true;

            try {
                const response = await axios.get('/api/v2/drivers/partners/payments/report', {
                    params: {
                        start: this.dataset.rangeDate.startDate,
                        end: this.dataset.rangeDate.endDate,
                        customer: this.dataset.selectedClient,
                        driver: this.dataset.selectedDriver
                    },
                    responseType: 'blob'
                });

                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'relatorio_motoristas_parceiros.xlsx');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

            } catch (err) {
                console.error('Erro ao baixar relatório de parceiros:', err);
                this.$dialog.notify.error("Erro ao exportar relatório de parceiros", {
                    position: "top-right",
                    timeout: 3000
                });
            } finally {
                this.dataset.exportingPartnerReport = false;
            }
        }

        // async loadLastUsers() {

        //     try {
        //         const result = await axios.get('/api/admin/dashboard/last-users', { params: {} });

        //         this.resolveChartLastUsers(result.data.content)

        //     } catch (error) {
        //         console.log('error ', error)
        //         this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
        //     }

        // },

    }
};
</script>

<style lang="scss" scoped>
// Mesmo visual da Visão geral do painel do cliente
.overview-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px 20px;

    h1 {
        color: #10213f;
        font-size: 28px;
        font-weight: 800;
        line-height: 1.2;
        margin: 0;
    }

    p {
        color: #52627d;
        font-size: 13px;
        margin: 8px 0 0;
    }
}

.overview-breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #71829d;
    font-size: 11px;
    margin-bottom: 8px;

    strong {
        color: #10213f;
        font-weight: 700;
    }
}

.overview-header-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;

    span {
        color: #667893;
        font-size: 12px;
        white-space: nowrap;
    }

    .v-btn--outlined {
        background: #fff !important;
    }
}

.overview-filter-card ::v-deep > .v-card__text {
    padding: 22px !important;
}

.overview-section-heading {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #10213f;
    margin-bottom: 18px;

    .v-icon {
        color: var(--v-primary-base);
    }

    strong {
        font-size: 16px;
        font-weight: 800;
    }
}

.overview-context {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 14px 0;
    padding: 12px 16px;
    background: #e7f0ff;
    border: 1px solid #bdd4ff;
    border-radius: 8px;
    color: #52627d;
    font-size: 13px;

    div {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
    }

    strong {
        color: #10213f;
        font-weight: 800;
    }

    button {
        background: transparent;
        border: 0;
        color: var(--v-primary-base);
        cursor: pointer;
        font-size: 12px;
        font-weight: 800;
    }
}

.section-card ::v-deep .v-card__text {
    padding-top: 0;
}
</style>
